import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { MeasurementProfile } from '@scs/domain-engine';
import { DEMO_DECORS } from '@scs/material-model';
import { MEASUREMENT_PROFILES, type CatalogLookup } from '@scs/export-contracts';
import { api } from './api';

/** Shapes as returned by GET /api/catalog (lengths in µm, money in minor units). */
export interface MapRef {
  assetId: string;
  coverageU: number;
  coverageV: number;
}
export interface DecorRow {
  id: string;
  manufacturer: string;
  code: string;
  name: string;
  finish: string;
  baseColor: string;
  roughness: number;
  hasGrain: boolean;
  maps: { baseColor?: MapRef; normal?: MapRef; roughness?: MapRef };
}
export interface BoardRow {
  id: string;
  decorId: string;
  substrate: string;
  thickness: number;
  sheetLength: number | null;
  sheetWidth: number | null;
  doubleSided: boolean;
  grainAlongLength: boolean;
}
export interface EdgeRow {
  id: string;
  manufacturer: string;
  code: string;
  name: string;
  decorId: string | null;
  material: string;
  thickness: number;
  width: number;
  finish: string;
}
export interface SupplierRow {
  id: string;
  name: string;
  notes: string;
}
export interface ArticleRow {
  id: string;
  supplierId: string;
  localCode: string;
  productKind: 'board' | 'edge';
  productId: string;
  priceMinor: number | null;
  currency: string;
  unit: 'm2' | 'sheet' | 'm' | 'piece';
  taxRatePermille: number | null;
  availability: string;
  checkedAt: string | null;
}
export interface ProfileRow {
  id: string;
  supplierId: string | null;
  name: string;
  measurement: Partial<MeasurementProfile>;
  limits: { maxLength?: number; maxWidth?: number; segmentBanding?: boolean; curvedBanding?: boolean };
  verified: boolean;
  verifiedNote: string;
}
export interface Catalog {
  suppliers: SupplierRow[];
  decors: DecorRow[];
  boards: BoardRow[];
  edges: EdgeRow[];
  articles: ArticleRow[];
  profiles: ProfileRow[];
}

export const EMPTY_CATALOG: Catalog = { suppliers: [], decors: [], boards: [], edges: [], articles: [], profiles: [] };

/** The active workspace catalog for property panels (provided by the editor / pages). */
export const CatalogContext = createContext<Catalog>(EMPTY_CATALOG);
export const useCatalogContext = () => useContext(CatalogContext);

export function useCatalog(): { catalog: Catalog; error: string | null; reload(): void } {
  const [catalog, setCatalog] = useState<Catalog>(EMPTY_CATALOG);
  const [error, setError] = useState<string | null>(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    let cancel = false;
    api<Catalog>('GET', '/api/catalog')
      .then((c) => !cancel && setCatalog(c))
      .catch((e) => !cancel && setError((e as Error).message));
    return () => {
      cancel = true;
    };
  }, [n]);
  return { catalog, error, reload: useCallback(() => setN((x) => x + 1), []) };
}

/** What the viewer needs to draw a decor. Texture tiles are in physical millimetres. */
export interface DecorView {
  id: string;
  label: string;
  baseColor: string;
  roughness: number;
  hasGrain: boolean;
  procedural?: { kind: 'wood' | 'solid' | 'concrete'; ringColor?: string; scaleMm?: number };
  maps: { baseColor?: { url: string; uMm: number; vMm: number }; normal?: { url: string; uMm: number; vMm: number }; roughness?: { url: string; uMm: number; vMm: number } };
  isDemo: boolean;
}

export function decorViews(cat: Catalog): DecorView[] {
  const map = (m?: MapRef) => (m ? { url: `/api/assets/${m.assetId}`, uMm: m.coverageU / 1000, vMm: m.coverageV / 1000 } : undefined);
  return [
    ...cat.decors.map((d) => ({
      id: d.id,
      label: `${d.manufacturer ? `${d.manufacturer} ` : ''}${d.code} ${d.name}`,
      baseColor: d.baseColor,
      roughness: d.roughness,
      hasGrain: d.hasGrain,
      maps: { baseColor: map(d.maps.baseColor), normal: map(d.maps.normal), roughness: map(d.maps.roughness) },
      isDemo: false,
    })),
    ...DEMO_DECORS.map((d) => ({ id: d.id, label: d.name, baseColor: d.baseColor, roughness: d.roughness, hasGrain: d.hasGrain, procedural: d.procedural, maps: {}, isDemo: true })),
  ];
}

export function lookupFor(cat: Catalog): CatalogLookup {
  const decors = new Map(cat.decors.map((d) => [d.id, d]));
  const edges = new Map(cat.edges.map((e) => [e.id, e]));
  return {
    decor: (id) => {
      const d = decors.get(id);
      if (d) return { code: d.manufacturer ? `${d.manufacturer} ${d.code}` : d.code, name: d.name };
      const demo = DEMO_DECORS.find((x) => x.id === id);
      return demo ? { code: demo.code, name: demo.name } : undefined;
    },
    edgeBand: (id) => {
      const e = edges.get(id);
      return e ? { code: e.code, name: e.name || e.code, thickness: e.thickness, width: e.width } : undefined;
    },
  };
}

export function profileFor(cat: Catalog, id: string): MeasurementProfile | undefined {
  const builtin = MEASUREMENT_PROFILES[id];
  if (builtin) return builtin;
  const p = cat.profiles.find((x) => x.id === id);
  return p ? ({ ...MEASUREMENT_PROFILES['order-finished']!, ...p.measurement, id: p.id, label: p.name } as MeasurementProfile) : undefined;
}

export function fmtPrice(a: ArticleRow): string {
  if (a.priceMinor === null) return 'cijena nepoznata';
  const unit = { m2: 'm²', sheet: 'tabla', m: 'm', piece: 'kom' }[a.unit];
  return `${(a.priceMinor / 100).toFixed(2)} ${a.currency}/${unit}`;
}
